import {isBoolean, isNil} from 'lodash/fp';
import {ChangeEvent, useCallback, useEffect, useMemo, useState} from 'react';

import {BasicTreeViewSelectionOptions, TreeViewSelection} from 'filters/features/FilterDialog/types';
import {BrokerMarketStatus, CarrierLevel} from 'modules/decisionsNext/api/types';

import {openFilterDialog} from 'filters/features/FilterDialog/emitter';
import {
  ALL_CARRIERS_ID,
  ALL_INTERMEDIARIES_ID,
  getSelectedCarriersByLevelWithOriginalIds,
  MarketNode,
  splitMarketItems,
} from 'modules/decisionsNext/hooks/trees/useMarketTree';
import {
  carrierViewTypesByLevel,
  getMarketInternalId,
  intermediaryViewTypesByLevel,
} from 'modules/decisionsNext/utils/marketUtils';
import {nullIfEmpty} from 'utils/arrayUtils';
import {pluralize} from 'utils/formatters';
import {useIsDownMd} from 'utils/hooks/useMediaQuery';
import {CarrierLevelBlock} from 'features/GlobalFilterProvider/filters/markets/CarrierLevelBlock';
import {carrierLevelLabelMap} from 'modules/decisionsNext/constants/carrierLevel';

import {GroupedIds, groupedIds, selectedByLevelToFilter} from 'components/TreePicker/utils/filterTreePickerUtils';

import {CounterTooltip} from 'filters/components/CounterTooltip';
import {SelectedTooltip} from 'filters/features/FilterDialog/components/SelectedTooltip';
import {
  brokerMarketStatusesOptions,
  MarketPlacement,
  marketPlacementOptions,
} from 'modules/decisionsNext/components/BaseFilter/constants';
import {CheckboxGroupBlock} from 'modules/decisionsNext/components/BaseFilter/Dialog/components/CheckboxGroupBlock';
import {RadioGroupBlock} from 'modules/decisionsNext/components/BaseFilter/Dialog/components/RadioGroupBlock';

type FieldValue = {
  direct?: boolean;
  carrierIds: GroupedIds;
  intermediaryIds: GroupedIds;
  brokerMarketStatuses: BrokerMarketStatus[] | null;
  carrierLevel?: CarrierLevel;
};

type MarketTreeViewOptions = BasicTreeViewSelectionOptions & {
  withCarrierLevel?: boolean;
};

const getInternalIds = ({ultimateParentIds, parentIds, childIds}: GroupedIds, type: 'carrier' | 'intermediary') =>
  [ultimateParentIds, parentIds, childIds].flatMap((group, index) =>
    group?.map(id =>
      getMarketInternalId(id, type === 'carrier' ? carrierViewTypesByLevel[index] : intermediaryViewTypesByLevel[index])
    )
  );

export const useMarketTreeViewSelection: TreeViewSelection<string, MarketNode, FieldValue, MarketTreeViewOptions> = (
  {loading, treeData, treeMap},
  {value, setValue},
  {disabled, withCarrierLevel = false}
) => {
  const isDownMd = useIsDownMd();

  const {carrierIds, intermediaryIds, direct, brokerMarketStatuses, carrierLevel} = value;
  const [isAllCarriersSelected, setIsAllCarriersSelected] = useState(false);
  const [isAllIntermediariesSelected, setIsAllIntermediariesSelected] = useState(false);

  useEffect(() => {
    if (!direct) {
      return;
    }

    if (isAllIntermediariesSelected) {
      setIsAllIntermediariesSelected(false);
    }

    if (intermediaryIds?.ultimateParentIds || intermediaryIds?.parentIds || intermediaryIds?.childIds) {
      setValue({
        ...value,
        intermediaryIds: {
          ultimateParentIds: undefined,
          parentIds: undefined,
          childIds: undefined,
        },
      });
    }
  }, [
    direct,
    intermediaryIds?.childIds,
    intermediaryIds?.parentIds,
    intermediaryIds?.ultimateParentIds,
    isAllIntermediariesSelected,
    setValue,
    value,
  ]);

  const selected = useMemo(() => {
    if (brokerMarketStatuses?.length) {
      const carriersByStatus =
        treeData
          .find(root => root.key === ALL_CARRIERS_ID)
          ?.children?.filter(
            carrier => !isNil(carrier?.companyStatus) && brokerMarketStatuses.includes(carrier.companyStatus)
          )
          ?.map(item => item.key) || [];
      const intermediariesByStatus = direct
        ? []
        : treeData
            .find(root => root.key === ALL_INTERMEDIARIES_ID)
            ?.children?.filter(
              carrier => !isNil(carrier?.companyStatus) && brokerMarketStatuses.includes(carrier.companyStatus)
            )
            ?.map(item => item.key) || [];

      return [...carriersByStatus, ...intermediariesByStatus];
    }

    const carriers = getInternalIds(carrierIds, 'carrier');
    const intermediaries = getInternalIds(intermediaryIds, 'intermediary');

    return [...carriers, ...intermediaries].filter(Boolean);
  }, [brokerMarketStatuses, carrierIds, intermediaryIds, treeData, direct]);

  const setSelectedPart = useCallback(
    (newSelected: string[], rootKey: typeof ALL_CARRIERS_ID | typeof ALL_INTERMEDIARIES_ID) => {
      const isCarrier = rootKey === ALL_CARRIERS_ID;
      const tree = treeData.find(item => item.key === rootKey)?.children || [];
      const groupedSelected = selectedByLevelToFilter(
        getSelectedCarriersByLevelWithOriginalIds(newSelected, treeData, tree),
        groupedIds
      );

      if (
        groupedSelected.ultimateParentIds?.length === tree.length &&
        !groupedSelected.parentIds?.length &&
        !groupedSelected.childIds?.length
      ) {
        isCarrier ? setIsAllCarriersSelected(true) : setIsAllIntermediariesSelected(true);

        return {
          ultimateParentIds: undefined,
          parentIds: undefined,
          childIds: undefined,
        };
      }

      isCarrier ? setIsAllCarriersSelected(false) : setIsAllIntermediariesSelected(false);

      return {
        ultimateParentIds: groupedSelected.ultimateParentIds ?? undefined,
        parentIds: groupedSelected.parentIds ?? undefined,
        childIds: groupedSelected.childIds ?? undefined,
      };
    },
    [treeData]
  );

  const setSelected = useCallback(
    (newSelected: string[]) => {
      const {selectedCarriers, selectedIntermediaries} = splitMarketItems(newSelected);

      setValue({
        carrierLevel: value.carrierLevel,
        direct: value.direct,
        brokerMarketStatuses: null,
        carrierIds: setSelectedPart(selectedCarriers, ALL_CARRIERS_ID),
        intermediaryIds: setSelectedPart(selectedIntermediaries, ALL_INTERMEDIARIES_ID),
      });
    },
    [setSelectedPart, setValue, value.carrierLevel, value.direct]
  );

  const hasStrategicCarriers = useMemo(
    () =>
      treeData.some(root =>
        root.children?.some(node =>
          [BrokerMarketStatus.Strategic, BrokerMarketStatus.NonStrategic].includes(
            node.companyStatus as BrokerMarketStatus
          )
        )
      ),
    [treeData]
  );

  return {
    selected: useMemo(() => {
      if (isAllCarriersSelected && isAllIntermediariesSelected) {
        return [ALL_CARRIERS_ID, ALL_INTERMEDIARIES_ID];
      }

      if (isAllCarriersSelected) {
        return [ALL_CARRIERS_ID, ...selected];
      }

      if (isAllIntermediariesSelected) {
        return [ALL_INTERMEDIARIES_ID, ...selected];
      }

      return selected;
    }, [isAllCarriersSelected, isAllIntermediariesSelected, selected]),
    setSelected,
    isDisabled: disabled || !!brokerMarketStatuses?.length,
    showSelectionCount: !disabled && !!brokerMarketStatuses?.length,
    disabledKeys: useMemo(() => (direct ? [ALL_INTERMEDIARIES_ID] : []), [direct]),
    selectionCount: useMemo(() => {
      const {selectedCarriers, selectedIntermediaries} = splitMarketItems(selected);
      const carriersCount = selectedCarriers.length;
      const intermediariesCount = selectedIntermediaries.length;

      const carriersTree = treeData.find(item => item.key === ALL_CARRIERS_ID)?.children || [];
      const intermediariesTree = treeData.find(item => item.key === ALL_INTERMEDIARIES_ID)?.children || [];

      const isAllCarriersSelected =
        !carriersCount || (brokerMarketStatuses?.length && carriersCount === carriersTree.length);
      const isAllIntermediariesSelected =
        !intermediariesCount || (brokerMarketStatuses?.length && intermediariesCount === intermediariesTree.length);

      const carriersTooltip = isAllCarriersSelected ? null : (
        <CounterTooltip
          total={`Carriers selected: ${carriersCount}`}
          groups={[
            {
              title: 'Ultimate Parent',
              count:
                carrierIds.ultimateParentIds?.length ??
                (brokerMarketStatuses?.length ? selectedCarriers.length : undefined),
            },
            {
              title: 'Parent',
              count: carrierIds.parentIds?.length,
            },
            {
              title: 'Issuing Paper',
              count: carrierIds.childIds?.length,
            },
          ]}
        />
      );

      const intermediariesTooltip = isAllIntermediariesSelected ? null : (
        <CounterTooltip
          total={`Intermediaries selected: ${intermediariesCount}`}
          groups={[
            {
              title: 'Ultimate Parent',
              count:
                intermediaryIds.ultimateParentIds?.length ??
                (brokerMarketStatuses?.length ? selectedIntermediaries.length : undefined),
            },
            {
              title: 'Parent',
              count: intermediaryIds.parentIds?.length,
            },
            {
              title: 'Intermediary',
              count: intermediaryIds.childIds?.length,
            },
          ]}
        />
      );

      if (direct) {
        return isAllCarriersSelected ? (
          'All policies placed directly with a carrier'
        ) : (
          <SelectedTooltip title={carriersTooltip}>{carriersCount} &#39;Direct&#39; to Carrier</SelectedTooltip>
        );
      }

      if (direct === false && isAllIntermediariesSelected) {
        const carriers = carriersCount
          ? `${carriersCount} ${pluralize(carriersCount, 'carrier', 'carriers')}`
          : 'carriers';

        return (
          <SelectedTooltip title={carriersTooltip}>All policies placed indirectly with {carriers}</SelectedTooltip>
        );
      }

      if (isAllCarriersSelected && isAllIntermediariesSelected) {
        return 'All';
      }

      return (
        <>
          <SelectedTooltip title={carriersTooltip}>Carriers: {carriersCount || 'All'}</SelectedTooltip>
          <SelectedTooltip title={intermediariesTooltip}>
            Intermediaries: {intermediariesCount || 'All'}
          </SelectedTooltip>
        </>
      );
    }, [
      selected,
      treeData,
      brokerMarketStatuses?.length,
      carrierIds.ultimateParentIds?.length,
      carrierIds.parentIds?.length,
      carrierIds.childIds?.length,
      intermediaryIds.ultimateParentIds?.length,
      intermediaryIds.parentIds?.length,
      intermediaryIds.childIds?.length,
      direct,
    ]),
    additionalFilters: (
      <>
        {withCarrierLevel && carrierLevel && (
          <CarrierLevelBlock
            carrierLevel={carrierLevel}
            setCarrierLevel={carrierLevel => setValue({...value, carrierLevel})}
          />
        )}

        <RadioGroupBlock
          row
          label='Placement'
          options={marketPlacementOptions}
          value={isNil(direct) ? MarketPlacement.All : direct ? MarketPlacement.Direct : MarketPlacement.Indirect}
          onChange={useCallback(
            (_: ChangeEvent<HTMLInputElement>, placementValue: string) => {
              const newDirect =
                placementValue === MarketPlacement.All ? undefined : placementValue === MarketPlacement.Direct;

              setValue({...value, direct: newDirect});
            },
            [setValue, value]
          )}
        />

        <CheckboxGroupBlock
          row={!isDownMd}
          dataTestId='carrier-type-block'
          label='Carrier Type'
          disabled={!hasStrategicCarriers || disabled}
          disabledMessage={
            !hasStrategicCarriers && !loading
              ? "Carrier type filters are disabled because they haven't been defined. Admins can set carrier types using the Market Mapping page in ennablData."
              : null
          }
          options={brokerMarketStatusesOptions}
          value={brokerMarketStatuses ?? []}
          onChange={useCallback(
            (statuses: BrokerMarketStatus[]) => {
              setIsAllCarriersSelected(false);
              setIsAllIntermediariesSelected(false);
              setValue({
                carrierLevel: value.carrierLevel,
                direct: value.direct,
                brokerMarketStatuses: nullIfEmpty(statuses),
                carrierIds: {
                  childIds: undefined,
                  parentIds: undefined,
                  ultimateParentIds: undefined,
                },
                intermediaryIds: {
                  childIds: undefined,
                  parentIds: undefined,
                  ultimateParentIds: undefined,
                },
              });
            },
            [setValue, value.carrierLevel, value.direct]
          )}
        />
      </>
    ),
    overrideActiveFilterContent: ({sectionId}) => {
      const carrierIds = getInternalIds(value.carrierIds, 'carrier').filter(Boolean);
      const intermediaryIds = getInternalIds(value.intermediaryIds, 'intermediary').filter(Boolean);

      return [
        withCarrierLevel &&
          value.carrierLevel &&
          value.carrierLevel !== CarrierLevel.UltimateParentCarrier && {
            disabled,
            id: 'carrier-level',
            label: 'Carrier Level',
            chips: [
              {
                id: value.carrierLevel,
                disabled,
                label: carrierLevelLabelMap[value.carrierLevel],
                onClick: () => openFilterDialog(sectionId),
                onDelete: () => setValue({...value, carrierLevel: CarrierLevel.UltimateParentCarrier}),
              },
            ],
          },
        isBoolean(value.direct) && {
          id: 'placement',
          label: 'Placement',
          chips: [
            {
              id: 'direct',
              label:
                marketPlacementOptions.find(option =>
                  direct ? option.value === MarketPlacement.Direct : option.value === MarketPlacement.Indirect
                )?.label || 'All',
              onClick: () => openFilterDialog(sectionId),
              onDelete: () => setValue({...value, direct: undefined}),
            },
          ],
        },
        value.brokerMarketStatuses?.length && {
          disabled,
          id: 'carrier-type',
          label: 'Carrier Type',
          chips: brokerMarketStatusesOptions
            .filter(option => (value.brokerMarketStatuses as BrokerMarketStatus[]).includes(option.value))
            .map(status => ({
              id: status.value,
              disabled,
              label: status.label,
              onClick: () => openFilterDialog(sectionId),
              onDelete: () =>
                setValue({
                  ...value,
                  brokerMarketStatuses: nullIfEmpty(
                    value.brokerMarketStatuses?.filter(statusValue => statusValue !== status.value)
                  ),
                }),
            })),
        },
        carrierIds.length > 0 && {
          disabled,
          id: 'carriers',
          label: 'Carriers',
          chips: carrierIds.map(id => ({
            id,
            disabled,
            label: treeMap.get(id)?.title || id,
            onClick: () => openFilterDialog(sectionId),
            onDelete: () => setSelected(selected.filter(key => key !== id)),
          })),
        },
        intermediaryIds.length > 0 && {
          disabled,
          id: 'intermediaries',
          label: 'Intermediaries',
          chips: intermediaryIds.map(id => ({
            id,
            disabled,
            label: treeMap.get(id)?.title || id,
            onClick: () => openFilterDialog(sectionId),
            onDelete: () => setSelected(selected.filter(key => key !== id)),
          })),
        },
      ];
    },
  };
};
